import { v4 as uuidV4 } from 'uuid';
import {
  HubOption, HUB_CONTINUE_SORT_ORDER, ConditionQuantifier, HubOptionStateValue,
} from 'dorian-shared/types/branch/HubOption';
import type {
  ReplayDefaults, ReplayCondition, TextDestCondition, IconCondition, TagCondition, BorderCondition, HubProgressBar,
} from 'dorian-shared/types/branch/HubOption';
import { createEmptySubCondition } from './stateConditionDefaults';

/**
 * Per-field character limits for Hub Step UI.
 * These will eventually come from the backend; kept as constants for easy migration.
 */
export const HUB_HEADER_MAX_LENGTH = 32;
export const HUB_SUBHEADER_MAX_LENGTH = 50;
export const HUB_CARD_TEXT_MAX_LENGTH = 12;
export const HUB_CARD_TEXT_WARN_LENGTH = 7;
export const HUB_CONTINUE_MAX_LENGTH = 18;
export const HUB_DISABLED_TEXT_MAX_LENGTH = 50;
export const HUB_CONTINUE_DISABLED_TEXT_MAX_LENGTH = 35;

export const HUB_MAX_PROGRESS_BARS = 4;
export const HUB_PROGRESS_BAR_LABEL_MAX_LENGTH = 12;
export const HUB_PROGRESS_BAR_LABEL_WARN_LENGTH = 7;

export function createDefaultReplayDefaults(): ReplayDefaults {
  return {
    state: HubOptionStateValue.Hidden, text: 'Replay', gotoBranchId: null, iconId: null,
  };
}

export function createDefaultContinueOption(): HubOption {
  return {
    uuid: uuidV4(),
    sortOrder: HUB_CONTINUE_SORT_ORDER,
    viewType: 'button',
    title: 'Continue',
    gotoBranchId: null,
    iconId: null,
    borderColor: null,
    state: { state: 'active' },
    score: null,
    replay: null,
    mods: [],
  };
}

export function createDefaultRegularOption(sortOrder: number, title = ''): HubOption {
  return {
    uuid: uuidV4(),
    sortOrder,
    viewType: 'card',
    title,
    gotoBranchId: null,
    iconId: null,
    borderColor: null,
    state: { state: 'active' },
    score: null,
    replay: null,
    mods: [],
  };
}

export function isReplayDefaultActive(replay: ReplayDefaults | null): boolean {
  return replay?.gotoBranchId != null;
}

export function createDefaultReplayCondition(): ReplayCondition {
  return {
    uuid: uuidV4(),
    quantifier: ConditionQuantifier.All,
    subConditions: [createEmptySubCondition()],
    resultState: null,
    resultIconId: null,
    resultText: null,
    resultGotoBranchId: null,
  };
}

export function createDefaultTextDestCondition(): TextDestCondition {
  return {
    uuid: uuidV4(),
    quantifier: ConditionQuantifier.All,
    subConditions: [createEmptySubCondition()],
    resultText: null,
    resultGotoBranchId: null,
  };
}

export function createDefaultIconCondition(): IconCondition {
  return {
    uuid: uuidV4(),
    quantifier: ConditionQuantifier.All,
    subConditions: [createEmptySubCondition()],
    resultIconId: null,
  };
}

export function createDefaultTagCondition(tagTemplateId: number): TagCondition {
  return {
    uuid: uuidV4(),
    quantifier: ConditionQuantifier.All,
    subConditions: [createEmptySubCondition()],
    tagTemplateId,
  };
}

export function createDefaultBorderCondition(color: string): BorderCondition {
  return {
    uuid: uuidV4(),
    quantifier: ConditionQuantifier.All,
    subConditions: [createEmptySubCondition()],
    color,
  };
}

export function createDefaultProgressBar(sortOrder: number): HubProgressBar {
  return {
    uuid: uuidV4(),
    numeratorUuid: uuidV4(),
    denominatorUuid: uuidV4(),
    label: '',
    sortOrder,
    numeratorType: 'variable',
    numeratorLiteral: null,
    numeratorVariableId: null,
    denominatorType: 'variable',
    denominatorLiteral: null,
    denominatorVariableId: null,
  };
}
